import React, { useEffect, useMemo, useRef, useState } from "react";
import { ForgotPassword } from "./Auth";
import { Html5Qrcode } from "html5-qrcode";
import api, {
  allyPortalApi,
  clearToken,
  getToken,
  setToken,
} from "./api";
import {
  Button,
  Card,
  Chip,
  EmptyState,
  Input,
  Logo,
  Textarea,
} from "./ui";

const BENEFIT_STATUS_LABELS = {
  active: "Activo",
  used: "Utilizado",
  cancelled: "Cancelado",
};

function benefitStatusTone(status) {
  if (status === "used") return "mint";
  if (status === "cancelled") return "coral";
  return "blue";
}

function extractValidationTarget(value) {
  const raw = String(value || "").trim();

  if (!raw) return null;

  try {
    const url = new URL(raw);
    const match = url.pathname.match(/^\/(benefit|transaction)\/([^/]+)\/?$/i);

    if (match) {
      return {
        kind: match[1].toLowerCase(),
        identifier: decodeURIComponent(match[2]),
      };
    }
  } catch {
    // Puede ser un código o token directo.
  }

  const pathMatch = raw.match(
    /(?:^|\/)(benefit|transaction)\/([^/?#]+)/i
  );

  if (pathMatch) {
    let identifier = pathMatch[2];

    try {
      identifier = decodeURIComponent(identifier);
    } catch {
      // Keep raw identifier.
    }

    return {
      kind: pathMatch[1].toLowerCase(),
      identifier,
    };
  }

  if (/^PWB-/i.test(raw)) {
    return {
      kind: "benefit",
      identifier: raw.toUpperCase(),
    };
  }

  if (/^PWC-/i.test(raw)) {
    return {
      kind: "transaction",
      identifier: raw.toUpperCase(),
    };
  }

  // Token directo legacy: conservamos compatibilidad con Transactions V1.
  return {
    kind: "transaction",
    identifier: raw,
  };
}

export default function AllyDashboard() {
  const [sessionChecked, setSessionChecked] = useState(false);
  const [allyUser, setAllyUser] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [activeTab, setActiveTab] = useState("home");

  const [dashboard, setDashboard] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [reviews, setReviews] = useState({ summary: null, items: [] });

  const [loading, setLoading] = useState(false);
  const [benefitsLoading, setBenefitsLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [qrValue, setQrValue] = useState("");
  const [qrError, setQrError] = useState("");

  async function loadDashboard() {
    setLoading(true);
    setError("");

    try {
      const data = await allyPortalApi.dashboard();
      setDashboard(data);
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) {
        clearToken();
        setAllyUser(null);
      }

      setError(
        e?.message || "No pudimos cargar el panel del aliado."
      );
    } finally {
      setLoading(false);
    }
  }

  async function loadBenefits() {
    setBenefitsLoading(true);

    try {
      const data = await allyPortalApi.benefits();
      setBenefits(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(
        e?.message || "No pudimos cargar los beneficios."
      );
    } finally {
      setBenefitsLoading(false);
    }
  }

  async function loadReviews() {
    setReviewsLoading(true);

    try {
      const data = await allyPortalApi.reviews();
      setReviews({
        summary: data?.summary || null,
        items: Array.isArray(data?.items) ? data.items : [],
      });
    } catch (e) {
      setError(
        e?.message || "No pudimos cargar las reseñas."
      );
    } finally {
      setReviewsLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      if (!getToken()) {
        if (mounted) setSessionChecked(true);
        return;
      }

      try {
        const user = await api.auth.me();

        if (!mounted) return;

        if (user?.role === "ally" && user?.ally_id) {
          setAllyUser(user);
        } else {
          clearToken();
        }
      } catch {
        clearToken();
      } finally {
        if (mounted) setSessionChecked(true);
      }
    }

    restoreSession();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!allyUser) return;

    loadDashboard();
    loadBenefits();
    loadReviews();
  }, [allyUser]);

  async function handleLogin(e) {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.auth.login({
        email: email.trim(),
        password,
      });

      if (
        res?.user?.role !== "ally" ||
        !res?.user?.ally_id
      ) {
        clearToken();
        setError(
          "Esta cuenta no está autorizada como aliado PawCita."
        );
        return;
      }

      setToken(res.token);
      setAllyUser(res.user);
      setPassword("");
    } catch (e2) {
      setError(
        e2?.message || "No pudimos iniciar sesión."
      );
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    clearToken();
    setAllyUser(null);
    setDashboard(null);
    setBenefits([]);
    setReviews({ summary: null, items: [] });
    setEmail("");
    setPassword("");
    setActiveTab("home");
    setError("");
  }

  function openValidationValue(value) {
    setQrError("");

    const target = extractValidationTarget(value);

    if (!target?.identifier) {
      setQrError(
        "No pudimos reconocer este QR o código de PawCita."
      );
      return;
    }

    const route =
      target.kind === "benefit"
        ? "/benefit/"
        : "/transaction/";

    window.location.href =
      `${route}${encodeURIComponent(target.identifier)}`;
  }

  function openValidation() {
    openValidationValue(qrValue);
  }

  const filteredBenefits = useMemo(() => {
    let list = benefits;

    if (statusFilter !== "all") {
      list = list.filter(
        (benefit) => benefit.status === statusFilter
      );
    }

    const term = search.trim().toLowerCase();

    if (term) {
      list = list.filter((benefit) =>
        [
          benefit.code,
          benefit.dog_name,
          benefit.benefit,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(term)
        )
      );
    }

    return list;
  }, [benefits, statusFilter, search]);

  if (!sessionChecked) {
    return (
      <Page>
        <Card className="p-8 text-center">
          <Logo />
          <p className="mt-5 text-sm text-slate-500">
            Verificando acceso…
          </p>
        </Card>
      </Page>
    );
  }

  if (!allyUser && showForgotPassword) {
    return (
      <div>
        <div className="mx-auto max-w-md px-4 pt-6 text-center">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
            PawCita Aliados
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Recupera el acceso autorizado de tu establecimiento.
          </p>
        </div>

        <ForgotPassword
          onBack={() => setShowForgotPassword(false)}
          onRequestCode={(forgotEmail) =>
            api.auth.forgotPassword(forgotEmail)
          }
          onReset={(forgotEmail, code, newPassword) =>
            api.auth.resetPassword(
              forgotEmail,
              code,
              newPassword
            )
          }
        />
      </div>
    );
  }

  if (!allyUser) {
    return (
      <Page narrow>
        <Card className="p-6 sm:p-8">
          <div className="text-center">
            <Logo size="lg" />
            <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
              Portal de aliados
            </p>

            <h1 className="mt-4 font-display text-3xl font-black text-[#0B2D4D]">
              Bienvenido
            </h1>

            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
              Ingresa con la cuenta autorizada de tu
              establecimiento.
            </p>
          </div>

          <form
            className="mt-7 space-y-4"
            onSubmit={handleLogin}
          >
            <Input
              label="Correo"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@establecimiento.com"
              testId="ally-dashboard-email"
            />

            <Input
              label="Contraseña"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              placeholder="••••••••"
              testId="ally-dashboard-password"
            />

            {error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : null}

            <Button
              type="submit"
              variant="navy"
              disabled={loading}
              className="w-full py-3.5"
              testId="ally-dashboard-login"
            >
              {loading
                ? "Ingresando…"
                : "Ingresar al panel"}
            </Button>

              <button
                type="button"
                onClick={() => {
                  setError("");
                  setShowForgotPassword(true);
                }}
                className="w-full text-center text-sm font-black text-[#0B2D4D] hover:underline"
                data-testid="ally-forgot-password"
              >
                ¿Olvidaste tu contraseña?
              </button>
          </form>

            <div className="mt-5">
              <InstallAllyApp
                testId="ally-install-login"
              />
            </div>
        </Card>
      </Page>
    );
  }

  const ally = dashboard?.ally || {};
  const metrics = dashboard?.metrics || {};

  return (
    <Page>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Logo />

          <div className="mt-3">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
              Panel de aliado
            </p>

            <h1 className="mt-1 font-display text-3xl font-black text-[#0B2D4D]">
              {ally.name || "Aliado PawCita"}
            </h1>

            <div className="mt-2">
              <Chip tone="mint">
                ✓ Aliado PawCita activo
              </Chip>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <InstallAllyApp
            compact
            testId="ally-install-header"
          />

          <Button
            variant="outline"
            onClick={logout}
            className="self-start"
            testId="ally-dashboard-logout"
          >
            Cerrar sesión
          </Button>
        </div>
      </header>

      <nav className="mb-6 grid grid-cols-2 gap-2 sm:flex sm:overflow-x-auto sm:pb-1">
        <TabButton
          active={activeTab === "home"}
          onClick={() => setActiveTab("home")}
        >
          Inicio
        </TabButton>

        <TabButton
          active={activeTab === "benefits"}
          onClick={() =>
            setActiveTab("benefits")
          }
        >
          Beneficios
        </TabButton>

        <TabButton
          active={activeTab === "reviews"}
          onClick={() => setActiveTab("reviews")}
        >
          Reseñas
        </TabButton>

        <TabButton
          active={activeTab === "validate"}
          onClick={() => setActiveTab("validate")}
        >
          Validar QR
        </TabButton>

        <TabButton
          active={activeTab === "commerce"}
          onClick={() => setActiveTab("commerce")}
        >
          Mi comercio
        </TabButton>
      </nav>

      {error ? (
        <div className="mb-5">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      ) : null}

      {activeTab === "home" ? (
        <HomeTab
          dashboard={dashboard}
          loading={loading}
          onValidate={() => setActiveTab("validate")}
          onBenefits={() =>
            setActiveTab("benefits")
          }
        />
      ) : null}

      {activeTab === "benefits" ? (
        <BenefitsTab
          benefits={filteredBenefits}
          loading={benefitsLoading}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          search={search}
          setSearch={setSearch}
        />
      ) : null}

      {activeTab === "reviews" ? (
        <ReviewsTab
          data={reviews}
          loading={reviewsLoading}
          onReload={loadReviews}
        />
      ) : null}

      {activeTab === "validate" ? (
        <ValidationTab
          qrValue={qrValue}
          setQrValue={setQrValue}
          qrError={qrError}
          onValidate={openValidation}
          onDetected={openValidationValue}
        />
      ) : null}

      {activeTab === "commerce" ? (
        <CommerceTab
          ally={ally}
          onSaved={(updatedAlly) =>
            setDashboard((current) =>
              current
                ? { ...current, ally: updatedAlly }
                : current
            )
          }
        />
      ) : null}
    </Page>
  );
}

function HomeTab({
  dashboard,
  loading,
  onValidate,
  onBenefits,
}) {
  const metrics = dashboard?.metrics || {};
  const recent = dashboard?.recent_benefits || [];

  if (loading && !dashboard) {
    return (
      <Card className="p-8 text-center">
        <p className="text-sm text-slate-500">
          Cargando panel…
        </p>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          label="Beneficios activos"
          value={metrics.active_benefits || 0}
          icon="🎁"
        />

        <MetricCard
          label="Beneficios utilizados"
          value={metrics.used_benefits || 0}
          icon="✓"
        />

        <MetricCard
          label="Beneficios registrados"
          value={metrics.total_benefits || 0}
          icon="🐾"
        />
      </div>

      <Card className="mt-5 overflow-hidden">
        <div className="bg-[#0B2D4D] p-6 text-white sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#67D5B5]">
              Validación
            </p>

            <h2 className="mt-2 font-display text-2xl font-black">
              ¿El cliente está en tu comercio?
            </h2>

            <p className="mt-2 max-w-xl text-sm leading-6 text-white/70">
              Valida su beneficio PawCita antes de completar la
              atención. PawCita no administra la agenda ni el pago
              del establecimiento.
            </p>
          </div>

          <Button
            variant="mint"
            onClick={onValidate}
            className="mt-5 w-full sm:mt-0 sm:w-auto"
            testId="ally-home-validate"
          >
            Escanear / validar QR
          </Button>
        </div>
      </Card>

      <div className="mt-7 flex items-center justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
            Actividad
          </p>

          <h2 className="mt-1 font-display text-2xl font-black text-[#0B2D4D]">
            Beneficios recientes
          </h2>
        </div>

        <button
          type="button"
          onClick={onBenefits}
          className="text-sm font-black text-[#0B2D4D]"
        >
          Ver todos →
        </button>
      </div>

      {recent.length ? (
        <div className="mt-4 space-y-3">
          {recent.map((benefit) => (
            <BenefitCard
              key={benefit.id || benefit.code}
              benefit={benefit}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            emoji="🎁"
            title="Aún no hay beneficios"
            text="Los beneficios PawCita activados por tus clientes aparecerán aquí."
          />
        </div>
      )}
    </>
  );
}


function BenefitsTab({
  benefits,
  loading,
  statusFilter,
  setStatusFilter,
  search,
  setSearch,
}) {
  return (
    <>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
          Historial
        </p>

        <h2 className="mt-1 font-display text-3xl font-black text-[#0B2D4D]">
          Beneficios
        </h2>

        <p className="mt-2 text-sm text-slate-500">
          Consulta los beneficios PawCita activados y utilizados
          en tu establecimiento.
        </p>
      </div>

      <div className="mt-5">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar código, mascota o beneficio…"
          testId="ally-benefits-search"
        />
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {[
          ["all", "Todos"],
          ["active", "Activos"],
          ["used", "Utilizados"],
          ["cancelled", "Cancelados"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatusFilter(key)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition ${
              statusFilter === key
                ? "border-[#0B2D4D] bg-[#0B2D4D] text-white"
                : "border-slate-200 bg-white text-slate-600"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <Card className="mt-5 p-8 text-center">
          <p className="text-sm text-slate-500">
            Cargando beneficios…
          </p>
        </Card>
      ) : benefits.length ? (
        <div className="mt-5 space-y-3">
          {benefits.map((benefit) => (
            <BenefitCard
              key={benefit.id || benefit.code}
              benefit={benefit}
              detailed
            />
          ))}
        </div>
      ) : (
        <div className="mt-5">
          <EmptyState
            emoji="🔎"
            title="Sin resultados"
            text="No encontramos beneficios con estos filtros."
          />
        </div>
      )}
    </>
  );
}


function ValidationTab({
  qrValue,
  setQrValue,
  qrError,
  onValidate,
  onDetected,
}) {
  return (
    <div className="mx-auto max-w-xl">
      <Card className="p-6 sm:p-8">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-50 text-3xl">
            ▦
          </div>

          <p className="mt-5 text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
            Validar beneficio
          </p>

          <h2 className="mt-2 font-display text-3xl font-black text-[#0B2D4D]">
            Escanea el QR PawCita
          </h2>

          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
            Abre la cámara y apunta al QR que muestra el cliente.
            PawCita reconocerá el beneficio automáticamente.
          </p>
        </div>

        <QrCameraScanner onDetected={onDetected} />

        <div className="my-7 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />

          <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
            o ingresa el código
          </span>

          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Input
          label="Código PawCita o enlace de validación"
          value={qrValue}
          onChange={(e) => setQrValue(e.target.value)}
          placeholder="Ej. PWB-B2F69C6F41"
          testId="ally-qr-value"
        />

        {qrError ? (
          <div className="mt-3">
            <ErrorMessage>{qrError}</ErrorMessage>
          </div>
        ) : null}

        <Button
          variant="navy"
          className="mt-5 w-full py-3.5"
          onClick={onValidate}
          testId="ally-qr-open"
        >
          Validar código manualmente
        </Button>

        <p className="mt-5 text-center text-xs leading-5 text-slate-400">
          El beneficio solo puede ser validado por la cuenta
          del comercio al que pertenece.
        </p>
      </Card>
    </div>
  );
}


function QrCameraScanner({ onDetected }) {
  const scannerRef = useRef(null);
  const handledRef = useRef(false);

  const [active, setActive] = useState(false);
  const [cameraError, setCameraError] = useState("");

  const readerId = "pawcita-ally-qr-reader";

  async function releaseScanner({
    updateState = true,
  } = {}) {
    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (scanner) {
      try {
        await scanner.stop();
      } catch {
        // Puede no haber llegado a iniciar.
      }

      try {
        await Promise.resolve(scanner.clear());
      } catch {
        // Limpieza defensiva.
      }
    }

    handledRef.current = false;

    if (updateState) {
      setActive(false);
    }
  }

  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      scannerRef.current = null;

      if (!scanner) return;

      scanner
        .stop()
        .catch(() => null)
        .finally(() => {
          try {
            scanner.clear();
          } catch {
            // Sin acción durante desmontaje.
          }
        });
    };
  }, []);

  async function startCamera() {
    if (scannerRef.current) return;

    setCameraError("");
    handledRef.current = false;
    setActive(true);

    // Permite que React pinte el contenedor antes
    // de inicializar el video.
    await new Promise((resolve) =>
      window.setTimeout(resolve, 0)
    );

    try {
      const cameras = await Html5Qrcode.getCameras();

      if (!cameras?.length) {
        throw new Error("No encontramos una cámara disponible.");
      }

      const preferredCamera =
        cameras.find((camera) =>
          /back|rear|environment|trasera/i.test(
            camera.label || ""
          )
        ) ||
        cameras[cameras.length - 1];

      const scanner = new Html5Qrcode(
        readerId,
        false
      );

      scannerRef.current = scanner;

      await scanner.start(
        preferredCamera.id,
        {
          fps: 10,
          qrbox: {
            width: 240,
            height: 240,
          },
        },
        async (decodedText) => {
          if (handledRef.current) return;

          handledRef.current = true;

          await releaseScanner();

          onDetected(decodedText);
        },
        () => {
          // Los frames sin QR son normales.
        }
      );
    } catch (error) {
      await releaseScanner();

      const insecure =
        typeof window !== "undefined" &&
        !window.isSecureContext;

      setCameraError(
        insecure
          ? "La cámara requiere una conexión segura (HTTPS). En producción PawCita Aliados funcionará con HTTPS."
          : "No pudimos abrir la cámara. Revisa el permiso de cámara del navegador e intenta nuevamente."
      );
    }
  }

  return (
    <div className="mt-6">
      <Button
        type="button"
        variant="mint"
        className="w-full py-3.5"
        onClick={
          active
            ? () => releaseScanner()
            : startCamera
        }
        testId="ally-qr-camera"
      >
        {active
          ? "Cerrar cámara"
          : "Abrir cámara para escanear"}
      </Button>

      <div
        className={
          active
            ? "mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-black p-2"
            : "hidden"
        }
      >
        <div
          id={readerId}
          className="overflow-hidden rounded-2xl"
        />
      </div>

      {active ? (
        <p className="mt-3 text-center text-xs font-bold text-emerald-700">
          Cámara activa · apunta al QR del cliente
        </p>
      ) : null}

      {cameraError ? (
        <div className="mt-3">
          <ErrorMessage>{cameraError}</ErrorMessage>
        </div>
      ) : null}
    </div>
  );
}



function ReviewsTab({ data, loading, onReload }) {
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");
  const [replyError, setReplyError] = useState("");

  const items = data?.items || [];
  const summary = data?.summary || {};

  function setDraft(review, value) {
    setDrafts((current) => ({
      ...current,
      [review.id]: value,
    }));
  }

  async function saveReply(review) {
    const text =
      drafts[review.id] !== undefined
        ? drafts[review.id]
        : review.merchant_reply?.text || "";

    if (String(text || "").trim().length < 2) {
      setReplyError("Escribe una respuesta antes de guardar.");
      return;
    }

    setSavingId(review.id);
    setReplyError("");

    try {
      await allyPortalApi.replyReview(review.id, String(text).trim());
      await onReload();
    } catch (e) {
      setReplyError(
        e?.message || "No pudimos guardar la respuesta."
      );
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="w-full">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
          Confianza PawCita
        </p>
        <h2 className="mt-1 font-display text-3xl font-black text-[#0B2D4D]">
          Reseñas
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          Consulta lo que comparte la comunidad y responde públicamente como establecimiento.
        </p>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Card className="p-5">
          <p className="text-sm font-bold text-slate-500">Calificación</p>
          <p className="mt-2 font-display text-4xl font-black text-[#0B2D4D]">
            {summary.count ? Number(summary.average || 0).toFixed(1) : "—"}
          </p>
          <p className="mt-1 text-sm font-black text-amber-500">
            ★★★★★
          </p>
        </Card>

        <Card className="p-5">
          <p className="text-sm font-bold text-slate-500">Opiniones</p>
          <p className="mt-2 font-display text-4xl font-black text-[#0B2D4D]">
            {summary.count || 0}
          </p>
        </Card>

        <Card className="p-5">
          <p className="text-sm font-bold text-slate-500">Visitas verificadas</p>
          <p className="mt-2 font-display text-4xl font-black text-[#0B2D4D]">
            {items.filter((review) => review.verified_visit).length}
          </p>
        </Card>
      </div>

      {replyError ? (
        <div className="mt-4">
          <ErrorMessage>{replyError}</ErrorMessage>
        </div>
      ) : null}

      <div className="mt-5 space-y-4">
        {loading && !items.length ? (
          <Card className="p-8 text-center">
            <p className="text-sm text-slate-500">Cargando reseñas…</p>
          </Card>
        ) : items.length ? (
          items.map((review) => {
            const draft =
              drafts[review.id] !== undefined
                ? drafts[review.id]
                : review.merchant_reply?.text || "";

            return (
              <Card key={review.id} className="p-5 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-black text-[#0B2D4D]">
                      {review.author_name || "Usuario"}
                    </p>
                    {review.verified_visit ? (
                      <p className="mt-1 text-xs font-black text-emerald-700">
                        ✓ Visita verificada por PawCita
                      </p>
                    ) : null}
                  </div>

                  <div className="rounded-full bg-amber-50 px-3 py-1 text-sm font-black text-[#0B2D4D]">
                    ★ {review.rating}
                  </div>
                </div>

                {review.comment ? (
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    {review.comment}
                  </p>
                ) : null}

                <div className="mt-5 rounded-2xl bg-[#F8FAFC] p-4">
                  <Textarea
                    label={
                      review.merchant_reply?.text
                        ? "Tu respuesta pública"
                        : "Responder públicamente"
                    }
                    value={draft}
                    onChange={(event) =>
                      setDraft(review, event.target.value)
                    }
                    maxLength={600}
                    placeholder="Agradece la visita o responde de forma profesional."
                  />

                  <div className="mt-3 flex justify-end">
                    <Button
                      onClick={() => saveReply(review)}
                      disabled={savingId === review.id}
                    >
                      {savingId === review.id
                        ? "Guardando…"
                        : review.merchant_reply?.text
                          ? "Actualizar respuesta"
                          : "Publicar respuesta"}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <EmptyState
            title="Aún no tienes reseñas"
            text="Cuando la comunidad comparta experiencias, aparecerán aquí."
          />
        )}
      </div>
    </div>
  );
}


function CommerceTab({ ally, onSaved }) {
  const makeForm = (source) => ({
    short_description: source?.short_description || "",
    long_description: source?.long_description || "",
    whatsapp: source?.whatsapp || "",
    phone: source?.phone || "",
    instagram: source?.instagram || "",
    website: source?.website || "",
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(makeForm(ally));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!editing) {
      setForm(makeForm(ally));
    }
  }, [ally, editing]);

  function setField(field) {
    return (event) => {
      setSaved(false);
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };
  }

  function beginEdit() {
    setForm(makeForm(ally));
    setSaveError("");
    setSaved(false);
    setEditing(true);
  }

  function cancelEdit() {
    setForm(makeForm(ally));
    setSaveError("");
    setSaved(false);
    setEditing(false);
  }

  async function saveProfile(event) {
    event.preventDefault();
    setSaving(true);
    setSaveError("");
    setSaved(false);

    try {
      const updated = await allyPortalApi.updateProfile(form);

      if (onSaved) {
        onSaved(updated);
      }

      setEditing(false);
      setSaved(true);
    } catch (e) {
      setSaveError(
        e?.message ||
          "No pudimos guardar los cambios. Inténtalo de nuevo."
      );
    } finally {
      setSaving(false);
    }
  }

  const category = ally?.category
    ? `${ally.category.charAt(0).toUpperCase()}${ally.category.slice(1)}`
    : "—";

  const protectedDetails = [
    ["Nombre comercial", ally?.name],
    ["Categoría", category],
    ["Ciudad", ally?.city],
    ["Zona", ally?.zone],
    ["Dirección", ally?.address],
    ["Beneficio PawCita", ally?.benefit],
  ];

  const contactDetails = [
    ["WhatsApp", ally?.whatsapp],
    ["Teléfono", ally?.phone],
    ["Instagram", ally?.instagram],
    ["Sitio web", ally?.website],
    ["Horario", ally?.hours],
  ].filter(([, value]) =>
    value !== undefined &&
    value !== null &&
    String(value).trim()
  );

  const locationLabel = [ally?.zone, ally?.city]
    .filter(Boolean)
    .join(", ");

  const previewAlly = editing
    ? { ...(ally || {}), ...form }
    : ally;

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
            Perfil del establecimiento
          </p>

          <h2 className="mt-1 font-display text-3xl font-black text-[#0B2D4D]">
            Mi comercio
          </h2>

          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Revisa cómo aparece tu establecimiento y mantén actualizada
            su información operativa.
          </p>
        </div>

        {!editing ? (
          <Button
            variant="outline"
            onClick={beginEdit}
          >
            Editar información
          </Button>
        ) : null}
      </div>

      {saved ? (
        <div className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
          Información actualizada correctamente.
        </div>
      ) : null}

      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          {editing ? (
            <Card className="p-5 sm:p-6">
              <form onSubmit={saveProfile}>
                <p className="font-display text-2xl font-black text-[#0B2D4D]">
                  Editar información
                </p>

                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Solo puedes modificar información operativa. Los datos
                  de identidad y clasificación son administrados por PawCita.
                </p>

                {saveError ? (
                  <div className="mt-4">
                    <ErrorMessage>{saveError}</ErrorMessage>
                  </div>
                ) : null}

                <div className="mt-5 grid gap-4">
                  <Textarea
                    label="Descripción corta"
                    rows={3}
                    maxLength={300}
                    value={form.short_description}
                    onChange={setField("short_description")}
                  />

                  <Textarea
                    label="Descripción del establecimiento"
                    rows={5}
                    maxLength={2000}
                    value={form.long_description}
                    onChange={setField("long_description")}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="WhatsApp"
                      value={form.whatsapp}
                      onChange={setField("whatsapp")}
                    />

                    <Input
                      label="Teléfono"
                      value={form.phone}
                      onChange={setField("phone")}
                    />

                    <Input
                      label="Instagram"
                      value={form.instagram}
                      onChange={setField("instagram")}
                    />

                    <Input
                      label="Sitio web"
                      value={form.website}
                      onChange={setField("website")}
                    />
                  </div>

                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Button
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? "Guardando…" : "Guardar cambios"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </Card>
          ) : (
            <>
              <Card className="overflow-hidden">
                <div className="border-b border-slate-100 p-5 sm:p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                        Información básica
                      </p>

                      <h3 className="mt-2 font-display text-2xl font-black text-[#0B2D4D]">
                        {ally?.name || "Aliado PawCita"}
                      </h3>

                      {ally?.short_description ? (
                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                          {ally.short_description}
                        </p>
                      ) : null}
                    </div>

                    <Chip tone="mint">
                      ✓ Aliado PawCita activo
                    </Chip>
                  </div>

                  {ally?.long_description ? (
                    <p className="mt-5 text-sm leading-6 text-slate-600">
                      {ally.long_description}
                    </p>
                  ) : null}
                </div>

                <div className="divide-y divide-slate-100">
                  {protectedDetails.map(([label, value]) => (
                    <div
                      key={label}
                      className="grid gap-1 px-5 py-4 sm:grid-cols-[180px_1fr] sm:px-6"
                    >
                      <p className="text-sm font-bold text-slate-400">
                        {label}
                      </p>

                      <p className="break-words text-sm font-bold text-[#0B2D4D]">
                        {value || "—"}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-5 sm:p-6">
                <p className="font-display text-xl font-black text-[#0B2D4D]">
                  Contacto y horario
                </p>

                {contactDetails.length ? (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {contactDetails.map(([label, value]) => (
                      <div key={label}>
                        <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                          {label}
                        </p>

                        <p className="mt-1 break-words text-sm font-bold text-[#0B2D4D]">
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">
                    Aún no hay información de contacto registrada.
                  </p>
                )}
              </Card>

              <Card className="p-5 sm:p-6">
                <p className="font-black text-[#0B2D4D]">
                  ¿Necesitas ayuda?
                </p>

                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Para soporte o consultas sobre tu perfil de aliado,
                  escríbenos a{" "}
                  <a
                    href="mailto:aliados@pawcita.com"
                    className="font-black text-[#0B2D4D] underline decoration-[#67D5B5] decoration-2 underline-offset-4"
                  >
                    aliados@pawcita.com
                  </a>
                </p>
              </Card>
            </>
          )}
        </div>

        <div className="space-y-4">
          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-black text-[#0B2D4D]">
                  Vista previa en PawCita
                </p>

                {editing ? (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-700">
                    En vivo
                  </span>
                ) : null}
              </div>
            </div>

            {ally?.image ? (
              <img
                src={ally.image}
                alt={ally?.name || "Comercio PawCita"}
                className="h-44 w-full object-cover"
              />
            ) : (
              <div className="flex h-32 items-center justify-center bg-[#FFF8F1] text-5xl">
                🏪
              </div>
            )}

            <div className="p-5">
              <p className="font-display text-xl font-black text-[#0B2D4D]">
                {ally?.name || "Aliado PawCita"}
              </p>

              <p className="mt-1 text-sm font-bold text-slate-400">
                {category}
              </p>

              {previewAlly?.short_description ? (
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {previewAlly.short_description}
                </p>
              ) : null}

              {locationLabel ? (
                <p className="mt-4 text-sm font-bold text-[#0B2D4D]">
                  📍 {locationLabel}
                </p>
              ) : null}

              {previewAlly?.whatsapp ? (
                <p className="mt-2 break-words text-sm font-bold text-[#0B2D4D]">
                  WhatsApp · {previewAlly.whatsapp}
                </p>
              ) : null}

              {previewAlly?.phone ? (
                <p className="mt-2 break-words text-sm font-bold text-[#0B2D4D]">
                  Teléfono · {previewAlly.phone}
                </p>
              ) : null}

              {previewAlly?.instagram ? (
                <p className="mt-2 break-words text-sm font-bold text-[#0B2D4D]">
                  Instagram · {previewAlly.instagram}
                </p>
              ) : null}

              {previewAlly?.website ? (
                <p className="mt-2 break-words text-sm font-bold text-[#0B2D4D]">
                  Web · {previewAlly.website}
                </p>
              ) : null}

              {ally?.hours ? (
                <p className="mt-2 break-words text-sm font-bold text-[#0B2D4D]">
                  Horario · {ally.hours}
                </p>
              ) : null}

              {ally?.benefit ? (
                <div className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3">
                  <p className="text-xs font-black uppercase tracking-wider text-emerald-700">
                    Beneficio PawCita
                  </p>

                  <p className="mt-1 text-sm font-black text-[#0B2D4D]">
                    {ally.benefit}
                  </p>
                </div>
              ) : null}
            </div>
          </Card>

          <Card className="p-5">
            <p className="font-black text-[#0B2D4D]">
              Información administrada por PawCita
            </p>

            <p className="mt-2 text-sm leading-6 text-slate-500">
              Para mantener el directorio ordenado y verificado, estos
              datos no pueden modificarse desde el portal.
            </p>

            <ul className="mt-3 space-y-2 text-sm font-bold text-slate-600">
              <li>• Nombre comercial</li>
              <li>• Categoría</li>
              <li>• Ciudad, zona y dirección</li>
              <li>• Ubicación validada en mapa</li>
              <li>• Beneficio PawCita</li>
              <li>• Horario de atención</li>
              <li>• Verificación y estado del aliado</li>
            </ul>
          </Card>

          <Card className="p-5">
            <p className="font-black text-[#0B2D4D]">
              ¿Necesitas cambiar un dato administrado?
            </p>

            <p className="mt-1 text-sm leading-6 text-slate-500">
              El equipo PawCita puede revisarlo y actualizarlo contigo.
            </p>

            <a
              href={`mailto:aliados@pawcita.com?subject=${encodeURIComponent(
                `Solicitud de cambio - ${ally?.name || "Aliado PawCita"} | PawCita`
              )}`}
              className="mt-4 inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-[#0B2D4D] transition hover:border-[#67D5B5] hover:bg-[#FFF8F1]"
            >
              Solicitar cambio
            </a>
          </Card>
        </div>
      </div>
    </div>
  );
}


function InstallAllyApp({
  compact = false,
  testId = "ally-install-app",
}) {
  const [installAvailable, setInstallAvailable] =
    useState(
      Boolean(
        typeof window !== "undefined" &&
          window.__pawcitaAllyInstallPrompt
      )
    );

  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.(
        "(display-mode: standalone)"
      )?.matches ||
      window.navigator.standalone === true;

    if (isStandalone) {
      setInstalled(true);
      return undefined;
    }

    const onReady = () => {
      setInstallAvailable(true);
    };

    const onInstalled = () => {
      setInstalled(true);
      setInstallAvailable(false);
      setShowHelp(false);
    };

    window.addEventListener(
      "pawcita-ally-install-ready",
      onReady
    );

    window.addEventListener(
      "pawcita-ally-installed",
      onInstalled
    );

    return () => {
      window.removeEventListener(
        "pawcita-ally-install-ready",
        onReady
      );

      window.removeEventListener(
        "pawcita-ally-installed",
        onInstalled
      );
    };
  }, []);

  if (installed) {
    if (compact) return null;

    return (
      <p className="text-center text-xs font-bold text-emerald-700">
        ✓ PawCita Aliados está instalada en este dispositivo.
      </p>
    );
  }

  async function handleInstall() {
    const prompt =
      window.__pawcitaAllyInstallPrompt;

    if (installAvailable && prompt) {
      await prompt.prompt();
      const choice = await prompt.userChoice;

      if (choice?.outcome === "accepted") {
        window.__pawcitaAllyInstallPrompt = null;
        setInstallAvailable(false);
      }

      return;
    }

    setShowHelp((current) => !current);
  }

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        onClick={handleInstall}
        className={compact ? "self-start" : "w-full"}
        testId={testId}
      >
        {compact
          ? "Instalar acceso"
          : "Instalar PawCita Aliados"}
      </Button>

      {showHelp ? (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-600">
          <p className="font-black text-[#0B2D4D]">
            Guardar PawCita Aliados en tu teléfono
          </p>

          <p className="mt-1">
            En iPhone: abre PawCita Aliados en Safari,
            toca Compartir y selecciona
            <span className="font-black">
              {" "}Añadir a pantalla de inicio
            </span>.
          </p>

          <p className="mt-2">
            En Android: abre el menú del navegador y
            selecciona
            <span className="font-black">
              {" "}Instalar app
            </span>
            {" "}o
            <span className="font-black">
              {" "}Agregar a pantalla principal
            </span>.
          </p>
        </div>
      ) : null}
    </div>
  );
}


function MetricCard({ label, value, icon }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-500">
            {label}
          </p>

          <p className="mt-2 font-display text-3xl font-black text-[#0B2D4D]">
            {value}
          </p>
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FDF7EA] text-xl font-black text-[#0B2D4D]">
          {icon}
        </div>
      </div>
    </Card>
  );
}

function BenefitCard({
  benefit,
  detailed = false,
}) {
  const date =
    benefit.status === "used"
      ? benefit.used_at || benefit.updated_at || benefit.created_at
      : benefit.created_at;

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-black text-[#0B2D4D]">
              {benefit.code}
            </p>

            <Chip tone={benefitStatusTone(benefit.status)}>
              {BENEFIT_STATUS_LABELS[benefit.status] ||
                benefit.status}
            </Chip>
          </div>

          <p className="mt-2 font-bold text-slate-800">
            {benefit.dog_name || "Mascota"}
          </p>

          <p className="mt-1 text-sm text-slate-500">
            {benefit.benefit || "Beneficio PawCita"}
          </p>

          {detailed && date ? (
            <p className="mt-2 text-xs text-slate-400">
              {new Date(date).toLocaleString("es-PA")}
            </p>
          ) : null}
        </div>

      </div>
    </Card>
  );
}


function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-black transition ${
        active
          ? "bg-[#0B2D4D] text-white"
          : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function ErrorMessage({ children }) {
  return (
    <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
      {children}
    </div>
  );
}

function Page({ children, narrow = false }) {
  return (
    <div className="min-h-screen bg-[#FDF7EA] px-4 pb-6 pt-[calc(1.5rem+env(safe-area-inset-top))] sm:px-6 sm:py-10">
      <div
        className={`mx-auto w-full ${
          narrow ? "max-w-md" : "max-w-6xl"
        }`}
      >
        {children}
      </div>
    </div>
  );
}
